import type { JSX } from 'react'
import type { Freshness, SourceIssue } from '../api'
import { ago, dayLabel, monthLabel, yen } from '../format'
import { isBehind, issueGap, jobLabel, sourceStatus } from '../freshness'

// The detail behind the top-bar pill: where each figure comes from, when it
// last loaded, and, for a sheet that failed its own checks, which cells to fix.

export function DataSourcesView({ freshness }: { freshness: Freshness[] }): JSX.Element {
  const broken = freshness.filter((source) => source.issues.length > 0)
  return (
    <>
      <section className="panel">
        <h2>Data sources</h2>
        <p className="chart-sub">
          Where each figure on this dashboard comes from, and when it last loaded.
        </p>
        <div className="scroller">
          <table className="sources-table">
            <thead>
              <tr>
                <th className="text">Source</th>
                <th className="text">Last loaded</th>
                <th className="text">Status</th>
              </tr>
            </thead>
            <tbody>
              {freshness.map((source) => {
                const status = sourceStatus(source)
                return (
                  <tr key={source.job}>
                    <td className="text">{jobLabel(source.job)}</td>
                    <td className="text" title={isBehind(source) ? (source.lastSucceededAt ?? undefined) : undefined}>
                      {source.lastSucceededAt === null ? 'Never' : `updated ${ago(source.lastSucceededAt)}`}
                    </td>
                    <td className="text">
                      <span className={`source-status ${status.tone}`}>{status.text}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>
      {broken.map((source) => (
        <SheetIssues key={source.job} source={source} />
      ))}
    </>
  )
}

// What to fix in the sheet, cell by cell, for whoever is reading the
// dashboard. The run refused to load the sheet, so the figures on the dashboard
// are from its last good run, and saying so is half the message.
function SheetIssues({ source }: { source: Freshness }): JSX.Element {
  const label = jobLabel(source.job)
  const asOf =
    source.lastSucceededAt === null
      ? 'Nothing has loaded from it yet.'
      : `Figures on the dashboard are from ${dayLabel(source.lastSucceededAt.slice(0, 10))}.`
  return (
    <section className="panel sheet-issues" role="alert">
      <h3>{label}: cells to fix</h3>
      <p>
        {label} wasn&apos;t loaded because the sheet&apos;s own totals don&apos;t add up. {asOf} Fix
        these cells in the sheet, then press Refresh data.
      </p>
      {/* Invariant #5: every figure here is a block subtotal, so the gap between
          two of them can equal one person's pay. The gap is derived from the two
          values already shown beside it (no new information), and the narrow
          exception that lets dashboard users see source_issues is documented at
          the grant in migration 20261001110000. */}
      <div className="scroller">
        <table className="sources-table">
          <thead>
            <tr>
              <th className="text">Month</th>
              <th className="text">Cell</th>
              <th>Sheet says</th>
              <th>Rows add up to</th>
              <th>Gap</th>
              <th className="text">Check</th>
            </tr>
          </thead>
          <tbody>
            {source.issues.map((issue) => (
              <tr key={`${issue.label}|${issue.month}|${issue.cell ?? ''}`}>
                <td className="text">{monthLabel(issue.month)}</td>
                <td className="text">
                  {issue.cell !== null && (
                    <CellRef tab={issue.tab} cell={issue.cell} url={issue.cellUrl} />
                  )}
                  {/* The sheet's own row label, so a cell ref alone never has to be
                      looked up to know which total it is. */}
                  <span className="issue-label">{issue.label.replace('|', ' ')}</span>
                </td>
                <td>{yen(issue.statedValue)}</td>
                <td>{yen(issue.computedValue)}</td>
                <td>{yen(issueGap(issue))}</td>
                <td className="text">
                  {issue.blockSubtotalCell !== null && issue.blockSubtotalValue !== null && (
                    <BlockSubtotal
                      issue={issue}
                      cell={issue.blockSubtotalCell}
                      value={issue.blockSubtotalValue}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function CellRef({ tab, cell, url }: { tab: string; cell: string; url: string | null }): JSX.Element {
  const text = `${tab}!${cell}`
  return url === null ? (
    <code>{text}</code>
  ) : (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <code>{text}</code>
    </a>
  )
}

// The block's own subtotal row settles which side is wrong. Agreeing with the
// rows means the summary cell is the odd one out (the 2026-09-14 case: a typed
// number in G6); agreeing with the summary cell means rows are what's off.
function BlockSubtotal({
  issue,
  cell,
  value,
}: {
  issue: SourceIssue
  cell: string
  value: string
}): JSX.Element {
  const ref = <CellRef tab={issue.tab} cell={cell} url={issue.blockSubtotalCellUrl} />
  if (value === issue.computedValue) {
    return (
      <>
        Row subtotal {ref} agrees with the rows, so check {issue.cell ?? 'the summary cell'} first
      </>
    )
  }
  if (value === issue.statedValue) {
    return (
      <>
        Row subtotal {ref} agrees with {issue.cell ?? 'the summary cell'}, so check the rows
      </>
    )
  }
  return (
    <>
      Row subtotal {ref} says {yen(value)}
    </>
  )
}
